/**
 * AdminManagement - Manage super_admin and sub_admin roles
 * Only accessible by super_admin role (not sub_admin)
 */

import React, { useState, useEffect } from 'react';
import { Users, Crown, Shield, CheckCircle, XCircle, AlertCircle, Loader2, Search, UserPlus, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import superAdminService from '../../services/superAdminService';
import { logger } from '../../utils/logger';

function AdminManagement() {
    const [loading, setLoading] = useState(true);
    const [admins, setAdmins] = useState([]);
    const [stats, setStats] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [newAdmin, setNewAdmin] = useState({
        email: '',
        name: '',
        password: '',
        confirm_password: '',
        role: 'sub_admin'
    });

    useEffect(() => {
        loadAdmins();
        loadStats();
    }, []);

    const loadAdmins = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await superAdminService.getAdmins();
            setAdmins(data);
        } catch (err) {
            logger.error('Error loading admins:', err);
            if (err.response?.status === 403) {
                setError('You do not have permission to manage admins. Only super administrators can access this page.');
            } else {
                setError('Failed to load admin list');
            }
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const statsData = await superAdminService.getAdminStats();
            setStats(statsData);
        } catch (err) {
            logger.error('Error loading stats:', err);
        }
    };

    const handleRoleChange = async (adminId, currentRole) => {
        try {
            setActionLoading(adminId);
            setError(null);
            setSuccess(null);

            const newRole = currentRole === 'super_admin' ? 'sub_admin' : 'super_admin';
            const confirmMessage = newRole === 'sub_admin'
                ? 'Are you sure you want to demote this administrator to sub_admin? They will lose access to admin management and platform settings.'
                : 'Are you sure you want to promote this administrator to super_admin? They will gain full platform access.';

            if (!window.confirm(confirmMessage)) {
                setActionLoading(null);
                return;
            }

            await superAdminService.updateAdminRole(adminId, newRole);
            setSuccess(`Administrator role updated to ${newRole}`);
            await loadAdmins();
            await loadStats();
        } catch (err) {
            logger.error('Error updating role:', err);
            setError(err.response?.data?.detail || 'Failed to update admin role');
        } finally {
            setActionLoading(null);
        }
    };

    const handleToggleStatus = async (adminId, currentStatus) => {
        try {
            setActionLoading(adminId);
            setError(null);
            setSuccess(null);

            const action = currentStatus ? 'deactivate' : 'activate';
            if (!window.confirm(`Are you sure you want to ${action} this administrator?`)) {
                setActionLoading(null);
                return;
            }

            await superAdminService.toggleAdminStatus(adminId);
            setSuccess(`Administrator ${action}d successfully`);
            await loadAdmins();
            await loadStats();
        } catch (err) {
            logger.error('Error toggling status:', err);
            setError(err.response?.data?.detail || 'Failed to update admin status');
        } finally {
            setActionLoading(null);
        }
    };

    const handleCreateAdmin = async (e) => {
        e.preventDefault();

        // Validation - email is used for login
        if (!newAdmin.email || !newAdmin.name || !newAdmin.password) {
            setError('Please fill in all required fields');
            return;
        }

        if (newAdmin.password !== newAdmin.confirm_password) {
            setError('Passwords do not match');
            return;
        }

        if (newAdmin.password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        try {
            setCreateLoading(true);
            setError(null);
            setSuccess(null);

            await superAdminService.createAdmin(newAdmin);
            setSuccess(`Administrator ${newAdmin.name} created successfully!`);

            // Reset form
            setNewAdmin({
                email: '',
                name: '',
                password: '',
                confirm_password: '',
                role: 'sub_admin'
            });
            setShowCreateForm(false);
            setShowPassword(false);
            setShowConfirmPassword(false);

            // Reload data
            await loadAdmins();
            await loadStats();
        } catch (err) {
            logger.error('Error creating admin:', err);
            setError(err.response?.data?.detail || 'Failed to create administrator');
        } finally {
            setCreateLoading(false);
        }
    };

    const filteredAdmins = admins.filter(admin =>
        admin.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (admin.name && admin.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const getRoleBadge = (role) => {
        if (role === 'super_admin') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                    <Crown className="h-3 w-3" />
                    Super Admin
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                <Shield className="h-3 w-3" />
                Sub Admin
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Users className="h-7 w-7 text-purple-600" />
                        Admin Management
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Manage administrator roles and permissions. Only super administrators can access this page.
                    </p>
                </div>
                <Button
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    className="bg-purple-600 hover:bg-purple-700"
                >
                    <UserPlus className="h-4 w-4 mr-2" />
                    {showCreateForm ? 'Cancel' : 'Add Admin'}
                </Button>
            </div>

            {/* Messages */}
            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                    <p className="text-red-800">{error}</p>
                </div>
            )}

            {success && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    <p className="text-green-800">{success}</p>
                </div>
            )}

            {/* Create Admin Form */}
            {showCreateForm && (
                <div className="mb-6 bg-white rounded-xl border-2 border-purple-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-6 py-4 border-b">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <UserPlus className="h-5 w-5 text-purple-600" />
                            Create New Administrator
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">Add a new super admin or sub admin to the platform</p>
                    </div>
                    <form onSubmit={handleCreateAdmin} className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium mb-2">Full Name *</label>
                                <Input
                                    value={newAdmin.name}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                                    placeholder="John Doe"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Email * (used for login)</label>
                                <Input
                                    type="email"
                                    value={newAdmin.email}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                                    placeholder="john@example.com"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Role *</label>
                                <select
                                    value={newAdmin.role}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    required
                                >
                                    <option value="sub_admin">Sub Admin - Limited access</option>
                                    <option value="super_admin">Super Admin - Full access</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Password *</label>
                                <div className="relative">
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        value={newAdmin.password}
                                        onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                                        placeholder="Minimum 6 characters"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Confirm Password *</label>
                                <div className="relative">
                                    <Input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={newAdmin.confirm_password}
                                        onChange={(e) => setNewAdmin({ ...newAdmin, confirm_password: e.target.value })}
                                        placeholder="Re-enter password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="mt-6 flex gap-3">
                            <Button
                                type="submit"
                                disabled={createLoading}
                                className="bg-purple-600 hover:bg-purple-700"
                            >
                                {createLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <UserPlus className="h-4 w-4 mr-2" />
                                        Create Administrator
                                    </>
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setShowCreateForm(false);
                                    setNewAdmin({
                                        email: '',
                                        name: '',
                                        password: '',
                                        confirm_password: '',
                                        role: 'sub_admin'
                                    });
                                    setError(null);
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </div>
            )}

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg border p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Total Admins</p>
                                <p className="text-2xl font-bold">{stats.total_admins}</p>
                            </div>
                            <Users className="h-8 w-8 text-purple-600" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Super Admins</p>
                                <p className="text-2xl font-bold text-yellow-600">{stats.super_admins}</p>
                            </div>
                            <Crown className="h-8 w-8 text-yellow-600" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Sub Admins</p>
                                <p className="text-2xl font-bold text-gray-600">{stats.sub_admins}</p>
                            </div>
                            <Shield className="h-8 w-8 text-gray-600" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Active</p>
                                <p className="text-2xl font-bold text-green-600">{stats.active_admins}</p>
                            </div>
                            <CheckCircle className="h-8 w-8 text-green-600" />
                        </div>
                    </div>
                </div>
            )}

            {/* Search */}
            <div className="mb-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by email or name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                </div>
            </div>

            {/* Admin List */}
            <div className="bg-white rounded-xl border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Admin
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Role
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Last Login
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {filteredAdmins.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                        {searchTerm ? 'No admins found matching your search' : 'No admins found'}
                                    </td>
                                </tr>
                            ) : (
                                filteredAdmins.map((admin) => (
                                    <tr key={admin.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div>
                                                <div className="font-medium">{admin.name || admin.email.split('@')[0]}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{admin.email}</td>
                                        <td className="px-6 py-4">{getRoleBadge(admin.role)}</td>
                                        <td className="px-6 py-4">
                                            {admin.is_active ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                                    <CheckCircle className="h-3 w-3" />
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                                    <XCircle className="h-3 w-3" />
                                                    Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {admin.last_login
                                                ? new Date(admin.last_login).toLocaleDateString()
                                                : 'Never'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleRoleChange(admin.id, admin.role)}
                                                    disabled={actionLoading === admin.id}
                                                >
                                                    {actionLoading === admin.id ? (
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                    ) : admin.role === 'super_admin' ? (
                                                        'Demote'
                                                    ) : (
                                                        'Promote'
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleToggleStatus(admin.id, admin.is_active)}
                                                    disabled={actionLoading === admin.id}
                                                    className={admin.is_active ? '' : 'text-green-600 hover:text-green-700'}
                                                >
                                                    {admin.is_active ? 'Deactivate' : 'Activate'}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Info Note */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                    <strong>Note:</strong> You cannot change your own role or deactivate yourself.
                    At least one super administrator must remain active at all times.
                </p>
            </div>
        </div>
    );
}

export default AdminManagement;
